import { createSlice, createAsyncThunk, createSelector, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store';
import { selectCurrentGroupId } from './groups_slice';
import { parseTimespanFilter, parseAgeFilter } from '../../_helpers/misc_functions';
import { fetchData } from './api';

type AsyncStatus = 'idle' | 'loading' | 'succeeded' | 'failed';

interface AsyncDataSlot {
  data: unknown;
  status: AsyncStatus;
  error: string | null;
}

interface BurnoutFilters {
  timespan: string;
  start_date?: string | null;
  end_date?: string | null;
  age?: string;
  gender?: string;
  department?: string;
  role?: string;
  specialty?: string;
  years_in_profession?: string;
  [key: string]: unknown;
}

interface ReportedBurnoutState {
  rollingBurnoutMetrics: AsyncDataSlot & {
    filters: { timespan: string };
    predictiveCost: AsyncDataSlot;
  };
  overallBurnoutMetrics: AsyncDataSlot & { filters: BurnoutFilters };
  topStressors: {
    comments: unknown[];
    topStressors: Record<string, unknown>;
    totalStressors: number;
    totalRespondees?: number;
    status: AsyncStatus;
    error: string | null;
  };
  significantBurnoutMetrics: AsyncDataSlot & { filters: { start_date: string | null; end_date: string | null } };
}

const initialState: ReportedBurnoutState = {
  rollingBurnoutMetrics: {
    filters: { timespan: '90 days' },
    data: null,
    status: 'idle',
    error: null,
    predictiveCost: { data: null, status: 'idle', error: null },
  },
  overallBurnoutMetrics: {
    data: null,
    status: 'idle',
    error: null,
    filters: {
      timespan: 'Custom',
      start_date: parseTimespanFilter({ timespan: '90 Days' }).start_date,
      end_date: parseTimespanFilter({ timespan: '90 Days' }).end_date,
      age: 'All',
      gender: 'All',
      department: 'All',
      role: 'All',
      specialty: 'All',
      years_in_profession: 'All',
    },
  },
  topStressors: {
    comments: [],
    topStressors: {},
    totalStressors: 0,
    status: 'idle',
    error: null,
  },
  significantBurnoutMetrics: {
    data: null,
    status: 'idle',
    error: null,
    filters: {
      start_date: parseTimespanFilter({ timespan: '90 Days' }).start_date,
      end_date: parseTimespanFilter({ timespan: '90 Days' }).end_date,
    },
  },
};

export const fetchRollingBurnoutMetrics = createAsyncThunk<unknown, void, { state: RootState }>(
  'reportedBurnout/fetchRollingBurnoutMetrics',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const { start_date, end_date } = parseTimespanFilter({ timespan: '90 Days' });
    const filters = { start_date, end_date, practice_group_id: selectCurrentGroupId(state) };
    try {
      return await fetchData({ endpoint: 'insights/reported_burnout/rolling_metrics', filters });
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch reported burnout data');
    }
  },
  {
    condition: (_, { getState }) =>
      getState().reported_burnout.rollingBurnoutMetrics.status !== 'loading',
  }
);

export const fetchPredictiveBurnoutCost = createAsyncThunk<unknown, { staffCount?: number }, { state: RootState }>(
  'reportedBurnout/fetchPredictiveBurnoutCost',
  async (thunkArg, { getState, rejectWithValue }) => {
    const state = getState();
    const { start_date, end_date } = parseTimespanFilter({ timespan: '90 Days' });
    const filters = {
      start_date, end_date,
      practice_group_id: selectCurrentGroupId(state),
      predictive_staff_count: thunkArg?.staffCount,
    };
    try {
      return await fetchData({ endpoint: 'insights/reported_burnout/predictive_cost', filters });
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch predictive cost data');
    }
  },
  {
    condition: (_, { getState }) =>
      getState().reported_burnout.rollingBurnoutMetrics.predictiveCost.status !== 'loading',
  }
);

export const fetchOverallBurnoutMetrics = createAsyncThunk<unknown, void, { state: RootState }>(
  'reportedBurnout/fetchOverallBurnoutMetrics',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const filters = state.reported_burnout.overallBurnoutMetrics.filters;
    const { start_date, end_date } = parseTimespanFilter(filters as any);
    const { start_age, end_age } = parseAgeFilter((filters as any).age);
    const allFilters = { ...filters, practice_group_id: selectCurrentGroupId(state), start_date, end_date, start_age, end_age };
    try {
      return await fetchData({ endpoint: 'insights/reported_burnout/filtered', filters: allFilters as any });
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch reported burnout data');
    }
  },
  {
    condition: (_, { getState }) =>
      getState().reported_burnout.overallBurnoutMetrics.status !== 'loading',
  }
);

export const fetchTopStressors = createAsyncThunk<any, void, { state: RootState }>(
  'reportedBurnout/fetchTopStressors',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const filters = state.reported_burnout.overallBurnoutMetrics.filters;
    const { start_date, end_date } = parseTimespanFilter(filters as any);
    const { start_age, end_age } = parseAgeFilter((filters as any).age);
    const allFilters = {
      ...filters,
      practice_group_id: selectCurrentGroupId(state),
      start_date, end_date, start_age, end_age,
      base_question_id: [
        parseInt(process.env.REACT_APP_1ST_STRESSOR as string),
        parseInt(process.env.REACT_APP_2ND_STRESSOR as string),
        parseInt(process.env.REACT_APP_3RD_STRESSOR as string),
      ],
    };
    try {
      return await fetchData({ endpoint: 'insights/reported_burnout/stressors', filters: allFilters as any });
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch top stressors data');
    }
  },
  {
    condition: (_, { getState }) =>
      getState().reported_burnout.topStressors.status !== 'loading',
  }
);

export const fetchSignificantBurnoutMetrics = createAsyncThunk<unknown, void, { state: RootState }>(
  'reportedBurnout/fetchSignificantBurnoutMetrics',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const filters = state.reported_burnout.significantBurnoutMetrics.filters;
    const { start_date, end_date } = parseTimespanFilter(filters as any);
    const allFilters = { practice_group_id: selectCurrentGroupId(state), start_date, end_date };
    try {
      return await fetchData({ endpoint: 'insights/reported_burnout/significant', filters: allFilters });
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch significant burnout data');
    }
  },
  {
    condition: (_, { getState }) =>
      getState().reported_burnout.significantBurnoutMetrics.status !== 'loading',
  }
);

const reportedBurnoutSlice = createSlice({
  name: 'reportedBurnout',
  initialState,
  reducers: {
    resetReportedBurnoutData: (state) => { Object.assign(state, initialState); },
    setOverallBurnoutMetricsFilters: (state, action: PayloadAction<Partial<BurnoutFilters>>) => {
      state.overallBurnoutMetrics.filters = { ...state.overallBurnoutMetrics.filters, ...action.payload };
    },
    setSignificantBurnoutMetricsFilters: (state, action: PayloadAction<Partial<{ start_date: string; end_date: string }>>) => {
      state.significantBurnoutMetrics.filters = { ...state.significantBurnoutMetrics.filters, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRollingBurnoutMetrics.pending, (state) => { state.rollingBurnoutMetrics.status = 'loading'; state.rollingBurnoutMetrics.error = null; })
      .addCase(fetchRollingBurnoutMetrics.fulfilled, (state, action) => { state.rollingBurnoutMetrics.status = 'succeeded'; state.rollingBurnoutMetrics.data = action.payload; })
      .addCase(fetchRollingBurnoutMetrics.rejected, (state, action) => { state.rollingBurnoutMetrics.status = 'failed'; state.rollingBurnoutMetrics.error = (action.payload as string) ?? action.error.message ?? null; })

      .addCase(fetchPredictiveBurnoutCost.pending, (state) => { state.rollingBurnoutMetrics.predictiveCost.status = 'loading'; state.rollingBurnoutMetrics.predictiveCost.error = null; })
      .addCase(fetchPredictiveBurnoutCost.fulfilled, (state, action) => { state.rollingBurnoutMetrics.predictiveCost.status = 'succeeded'; state.rollingBurnoutMetrics.predictiveCost.data = action.payload; })
      .addCase(fetchPredictiveBurnoutCost.rejected, (state, action) => { state.rollingBurnoutMetrics.predictiveCost.status = 'failed'; state.rollingBurnoutMetrics.predictiveCost.error = (action.payload as string) ?? action.error.message ?? null; })

      .addCase(fetchOverallBurnoutMetrics.pending, (state) => { state.overallBurnoutMetrics.status = 'loading'; state.overallBurnoutMetrics.error = null; })
      .addCase(fetchOverallBurnoutMetrics.fulfilled, (state, action) => { state.overallBurnoutMetrics.status = 'succeeded'; state.overallBurnoutMetrics.data = action.payload; })
      .addCase(fetchOverallBurnoutMetrics.rejected, (state, action) => { state.overallBurnoutMetrics.status = 'failed'; state.overallBurnoutMetrics.error = (action.payload as string) ?? action.error.message ?? null; })

      .addCase(fetchTopStressors.pending, (state) => { state.topStressors.status = 'loading'; state.topStressors.error = null; })
      .addCase(fetchTopStressors.fulfilled, (state, action) => {
        state.topStressors.status = 'succeeded';
        state.topStressors.comments = action.payload.comments;
        state.topStressors.topStressors = action.payload.top_stressors;
        state.topStressors.totalStressors = action.payload.total_stressors;
        state.topStressors.totalRespondees = action.payload.total_respondees;
      })
      .addCase(fetchTopStressors.rejected, (state, action) => { state.topStressors.status = 'failed'; state.topStressors.error = (action.payload as string) ?? action.error.message ?? null; })

      .addCase(fetchSignificantBurnoutMetrics.pending, (state) => { state.significantBurnoutMetrics.status = 'loading'; state.significantBurnoutMetrics.error = null; })
      .addCase(fetchSignificantBurnoutMetrics.fulfilled, (state, action) => { state.significantBurnoutMetrics.status = 'succeeded'; state.significantBurnoutMetrics.data = action.payload; })
      .addCase(fetchSignificantBurnoutMetrics.rejected, (state, action) => { state.significantBurnoutMetrics.status = 'failed'; state.significantBurnoutMetrics.error = (action.payload as string) ?? action.error.message ?? null; });
  },
});

export const { resetReportedBurnoutData, setOverallBurnoutMetricsFilters, setSignificantBurnoutMetricsFilters } = reportedBurnoutSlice.actions;
export default reportedBurnoutSlice.reducer;

export const getComments = createSelector(
  (state: { reported_burnout: ReportedBurnoutState }) => state.reported_burnout.topStressors.comments,
  (comments) => ((comments as any[]) || []).filter((comment: any) => comment.comment)
);
